import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { FaStar, FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaCheck } from "react-icons/fa";
import { ACTIVITY_API_BASE, getActivityImageUrl, fetchActivityDetail } from "../lib/activityApi";
import BookingWidget from "../components/activities/BookingWidget";

const baseUrl = ACTIVITY_API_BASE.replace("/api/activity", "");

export default function ActivityDetailPage() {
  const { activityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bookingDone, setBookingDone] = useState(false);

  const state = location.state || {};
  const cityIdFromState = state.cityId;
  const countryFromState = state.country;
  const cityFromState = state.city;
  const cityId = activity?.cityId?._id ?? cityIdFromState;
  const country = activity?.cityId?.countryId ?? countryFromState;
  const city = activity?.cityId ?? cityFromState;

  useEffect(() => {
    if (!activityId) return;
    setLoading(true);
    if (cityId) {
      axios.get(`${ACTIVITY_API_BASE}/user/activities/${cityId}`).then((res) => {
        const list = res.data?.data || [];
        const a = list.find((x) => String(x._id) === String(activityId));
        setActivity(a || null);
      }).catch(() => setActivity(null)).finally(() => setLoading(false));
    } else {
      fetchActivityDetail(activityId).then(setActivity).catch(() => setActivity(null)).finally(() => setLoading(false));
    }
  }, [activityId, cityId]);

  const images = activity?.images?.length ? activity.images : [];
  const imageUrl = (path) => getActivityImageUrl(path, baseUrl);

  const handleBookingComplete = () => {
    setBookingDone(true);
  };

  if (!activityId) {
    navigate("/activities", { replace: true });
    return null;
  }

  if (loading && !activity) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1ABCE3]" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Activity not found.</p>
          <button
            type="button"
            onClick={() => navigate("/activities")}
            className="px-4 py-2 rounded-xl font-medium text-white hover:opacity-90"
            style={{ backgroundColor: "#1ABCE3" }}
          >
            Back to Activities
          </button>
        </div>
      </div>
    );
  }

  if (bookingDone) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <FaCheck className="text-3xl text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-[#222222] mb-2">Booking request sent</h2>
          <p className="text-gray-600 mb-6">We will confirm your booking shortly.</p>
          <button
            type="button"
            onClick={() => navigate("/activities")}
            className="px-6 py-3 rounded-xl font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: "#1ABCE3" }}
          >
            Back to Activities
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <button
          type="button"
          onClick={() => navigate(cityId ? `/activities/${country?._id}/${cityId}` : "/activities")}
          className="flex items-center gap-2 text-gray-600 hover:text-[#1ABCE3] mb-6"
        >
          <FaChevronLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Gallery */}
            <div className="relative rounded-2xl overflow-hidden bg-slate-900 min-h-[300px] md:min-h-[400px]">
              {images.length > 0 ? (
                <>
                  <img
                    src={imageUrl(images[galleryIndex])}
                    alt={activity.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setGalleryIndex((i) => (i - 1 + images.length) % images.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                      >
                        <FaChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setGalleryIndex((i) => (i + 1) % images.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                      >
                        <FaChevronRight className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setGalleryIndex(i)}
                            className={`w-2 h-2 rounded-full transition-all ${i === galleryIndex ? "w-6 bg-white" : "bg-white/50"}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <img
                  src={getActivityImageUrl(null, baseUrl)}
                  alt={activity.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              <div className="absolute bottom-4 left-4 right-4">
                <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{activity.name}</h1>
                {city && (
                  <p className="flex items-center gap-2 text-white/90 mt-1">
                    <FaMapMarkerAlt className="w-4 h-4" /> {city.name}
                    {country && `, ${country.name}`}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#222222] mb-3">Description</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                {activity.description || "No description available."}
              </p>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#222222] mb-3">Highlights</h2>
              <ul className="space-y-2">
                {activity.highlights?.length
                  ? activity.highlights.map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <FaCheck className="w-4 h-4 text-green-500 shrink-0" /> {h}
                      </li>
                    ))
                  : (
                      <>
                        <li className="flex items-center gap-2 text-gray-600"><FaCheck className="w-4 h-4 text-green-500" /> Expert guides</li>
                        <li className="flex items-center gap-2 text-gray-600"><FaCheck className="w-4 h-4 text-green-500" /> Instant confirmation</li>
                        <li className="flex items-center gap-2 text-gray-600"><FaCheck className="w-4 h-4 text-green-500" /> Free cancellation</li>
                      </>
                    )}
              </ul>
            </div>

            {/* Rating placeholder */}
            <div className="flex items-center gap-2 text-gray-600">
              <FaStar className="w-5 h-5 text-amber-500" />
              <span className="font-semibold">4.9</span>
              <span className="text-sm">(175 Reviews)</span>
            </div>
          </div>

          <div className="lg:col-span-1">
            <BookingWidget
              activityId={activity._id}
              activity={activity}
              country={country}
              onBookingComplete={handleBookingComplete}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
